import type { CustomNode, RuleOrElseCondition } from '@src/components/custom-nodes/types';
import { v4 as uuid } from 'uuid';
import { NODES_GAP, NODE_HEIGHT } from '@src/consts';
import { updateNode, deleteNode } from '@src/scenario-editor/utils';
import type { ReactFlowInstance } from 'reactflow';

interface GetBehaviorRulesBranchNodeProps {
  reactFlowInstance?: ReactFlowInstance;
  position: { x: number; y: number };
  title?: string;
  branchIndex: number;
  parentNodeId: string;
  parentBranchId: string;
}
export const getBehaviroRuleBranchNode = ({
  position,
  title = '',
  branchIndex,
  parentNodeId,
  parentBranchId,
}: GetBehaviorRulesBranchNodeProps) => {
  const newIfBranchNode = {
    id: uuid(),
    type: 'IfBranchNode',
    selectable: false,
    position: {
      ...position,
      y: position.y + NODE_HEIGHT + NODES_GAP,
    },
    data: {
      label: title + `-${branchIndex}`,
      handles: {
        t: true,
        b: true
      },
      message: `Option ${branchIndex}`,
      nodeType: 'if:branch',
      dependsOn: parentNodeId
    },
    branchId: parentBranchId + `-${branchIndex}`,
    parentBranchId,
    parentNodeId,
    style: {
      pointerEvents: 'none',
    },
  };

  return newIfBranchNode as CustomNode;
}

interface GetBehaviorRulesNodeProps {
  reactFlowInstance?: ReactFlowInstance;
  position: { x: number; y: number };
  title?: string;
  parentNodeId: string;
  parentBranchId: string;
  conditions?: RuleOrElseCondition[];
  branchId?: string;
}
export const getBehaviorRulesNode = ({
  position,
  title = '',
  parentNodeId,
  parentBranchId,
  conditions,
  branchId,
}: GetBehaviorRulesNodeProps) => {
  const newIfConditionNode = {
    id: uuid(),
    type: 'IfConditionBehaviorNode',
    selected: true,
    position,
    data: {
      title,
      handles: {
        t: true,
        b: true
      },
      message: '',
      nodeType: 'if:condition-behavior',
      conditions,
    },
    branchId,
    parentBranchId,
    parentNodeId,
  };

  return newIfConditionNode as CustomNode;
}

export const getBehaviorRulesCloseNode = ({
  reactFlowInstance,
  position,
  title = '',
  parentNodeId,
  parentBranchId,
  branchId,
}: Exclude<GetBehaviorRulesNodeProps, 'conditions'>) => {
  const updateItem = updateNode({ reactFlowInstance });
  const deleteItem = deleteNode({ reactFlowInstance });

  const newNodeClose = {
    id: uuid(),
    type: 'IfCloseNode',
    selectable: false,
    position: {
      ...position,
      y: position.y + 2*NODE_HEIGHT + 2*NODES_GAP,
    },
    data: {
      label: title + '-close',
      handles: {
        l: true,
        r: true,
        t: true,
        b: true,
      },
      message: 'Behavior rules close',
      nodeType: 'if:condition-close',
      updateItem,
      deleteItem,
      dependsOn: parentNodeId,
    },
    branchId,
    parentBranchId,
    parentNodeId,
    style: {
      pointerEvents: 'none',
    },
  };

  return newNodeClose as CustomNode;
};

export const getBehaviorRulesBlock = ({
  reactFlowInstance,
  position,
  title,
  parentNodeId,
  parentBranchId,
  conditions,
  branchId,
}: GetBehaviorRulesNodeProps) => {
  const ifConditionNode = getBehaviorRulesNode({
    reactFlowInstance,
    position,
    title,
    parentNodeId,
    parentBranchId,
    conditions,
    branchId,
  });
  const ifConditionNodeClose = getBehaviorRulesCloseNode({
    reactFlowInstance,
    position,
    title,
    parentNodeId: ifConditionNode.id,
    parentBranchId,
    branchId,
  });

  return {
    ifConditionNode,
    ifBranchNodes: conditions!.map((_, index) => getBehaviroRuleBranchNode({
      reactFlowInstance,
      position,
      title,
      branchIndex: index + 1,
      parentNodeId: ifConditionNode.id,
      parentBranchId: ifConditionNode.branchId,
    })),
    ifConditionNodeClose,
  };
};
